import { Box } from "@chakra-ui/react";
import {
  colors,
  ColorIcon,
  customEvent,
  FUNERAL_SIDO_LIST,
  FUNERAL_SIGUNGU,
  Spacing,
} from "goi_common";
import { useEffect, useMemo, useState } from "react";
import type { ParlorOption } from "@/types/parlorOption";
import { MubinsoConversionScreen } from "./MubinsoConversionScreen";
import { MubinsoLeadModal } from "./MubinsoLeadModal";
import MubinsoParlorPickScreen from "./MubinsoParlorPickScreen";
import { MubinsoPickedParlorsRecap } from "./MubinsoPickedParlorsRecap";
import { MubinsoCtaButton, MubinsoInfoBox, MubinsoScreenLayout } from "./MubinsoUI";
import { buildMubinsoLeadMessage } from "./mubinsoLeadMessage";
import { FAMILY_TRACK_RECORD, mubinsoEntryTrigger, mubinsoLeadStatus } from "./mubinsoConstants";

/**
 * 진행바에 쓰는 스텝 수.
 *
 * B안의 관심 장례식장 체크가 붙어도 이 값은 3 그대로다. 진행바는 info1·info2 에서만 뜨고
 * 지역 선택과 관심 장례식장 체크는 둘 다 진행바 없는 선택 화면이기 때문이다. 여기를 4로
 * 올리면 B 배정자만 "1/4, 2/4" 를 보게 돼, 배정을 모르는 사용자에게 스텝이 늘어난 것처럼
 * 읽힌다.
 */
const TOTAL_STEPS = 3;

const SIDO_LIST = FUNERAL_SIDO_LIST.map((sido) => ({
  code: sido[0] as number,
  name: sido[1] as string,
}));

type ParlorFindScreen = "info1" | "info2" | "region" | "parlorPick" | "conversion";

interface MubinsoParlorFindFunnelProps {
  isUrgent: boolean;
  situationAnswer: string;
  topicAnswer: string;
  onComplete: () => void;
  /** 관심 장례식장 체크 화면(B안)용 전국 경량 목록. 페이지 getStaticProps 가 내려준다 */
  parlorOptions?: ParlorOption[];
  /**
   * 관심 장례식장 체크 A/B 의 B 배정 여부. 배정은 Q2(상황) 화면에서 끝나고 여기로 내려온다.
   * 임박/임종을 이 퍼널로 보낼지도 같은 값이 가른다(MubinsoFunnelContainer 의 resolveFunnel).
   */
  isPickVariant: boolean;
  /** M5 가족장 경로. 고이 실적 문구를 「장례 5,000건」으로 바꾼다(첫 화면과 같은 숫자) */
  family?: boolean;
}

/**
 * 무빈소 장례식장 찾기 퍼널: 불가능한 장례식장 → 장례식장 별 차이 → 지역선택(시도→시군구) → 전환.
 * 리드 메시지에 "희망 지역 : {시도} {시군구}" 포함.
 *
 * B안은 지역선택과 전환 사이에 관심 장례식장 체크가 들어간다.
 */
export default function MubinsoParlorFindFunnel({
  isUrgent,
  situationAnswer,
  topicAnswer,
  onComplete,
  parlorOptions = [],
  isPickVariant,
  family = false,
}: MubinsoParlorFindFunnelProps) {
  // E4 실험(369)에서 찾기 퍼널도 A · B 를 같은 축으로 보려고 진입별 개별 이벤트(fnd)를 쏜다.
  // 가족장은 369 밖이고 가족장 실험에 없던 이벤트라 쏘지 않는다(fnd 에 섞이지도 않게)
  const entry = family ? undefined : "topic_parlor";
  const [screen, setScreen] = useState<ParlorFindScreen>("info1");
  const [regionStep, setRegionStep] = useState<1 | 2>(1);
  const [sido, setSido] = useState<{ code: number; name: string } | null>(null);
  const [region, setRegion] = useState("");
  const [sigunguCode, setSigunguCode] = useState<number | null>(null);
  const [selectedParlorIds, setSelectedParlorIds] = useState<number[]>([]);
  const [openModal, setOpenModal] = useState<null | "info" | "consult">(null);

  /** 고른 시군구의 장례식장. 시군구를 고르기 전에는 비어 있다 */
  const pickCandidates = useMemo(() => {
    if (!sigunguCode) return [];
    return parlorOptions.filter((parlor) => String(parlor.sigungu) === String(sigunguCode));
  }, [parlorOptions, sigunguCode]);

  /** 되짚기 줄이 사진을 쓰므로 이름만이 아니라 고른 항목을 통째로 들고 간다 */
  const selectedParlors = useMemo(
    () => pickCandidates.filter((parlor) => selectedParlorIds.includes(parlor.id)),
    [pickCandidates, selectedParlorIds],
  );

  /** 슬랙 메시지에는 줄이지 않고 전부 싣는다. 상담사는 고른 곳을 다 알아야 한다 */
  const selectedParlorNames = useMemo(
    () => selectedParlors.map((parlor) => parlor.name),
    [selectedParlors],
  );

  // 화면/지역단계 전환 시 스크롤 최상단(시도→시군구 포함).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [screen, regionStep]);

  // 퍼널별 단계 진행(어느 페이지까지 봤는지). info1→1, info2→2, region→3 (전환은 mubinso_request_complete).
  useEffect(() => {
    const stepMap: Record<string, number> = { info1: 1, info2: 2, region: 3, parlorPick: 4 };
    const step = stepMap[screen];
    if (step) {
      customEvent({
        newGtm: { action: "mubinso_funnel_step", properties: { funnel: "parlorFind", step } },
      });
    }
  }, [screen]);

  const sigunguList = useMemo(() => {
    if (!sido) return [];
    return Array.from(FUNERAL_SIGUNGU.get(sido.code) ?? []).map((sigungu) => {
      const [code, name] = sigungu as [number, string];
      return { code, name };
    });
  }, [sido]);

  if (screen === "info1") {
    return (
      <MubinsoScreenLayout
        progress={{ current: 1, total: TOTAL_STEPS }}
        contentBottomGap={56}
        footer={
          <MubinsoCtaButton
            label="다음"
            onClick={() => {
              customEvent({
                newGtm: { path: "mubinso_funnel", trigger_and_target: "funnel-next-1" },
              });
              setScreen("info2");
            }}
          />
        }
      >
        <Box width="100%" whiteSpace="pre-line" className="head2_b black">
          무빈소 장례가 <span style={{ color: colors.orange650 }}>불가능한</span>
          <br />
          장례식장도 많아요
        </Box>
        <Spacing gap={32} />
        <MubinsoInfoBox label="왜 그런가요?">
          {
            "무빈소 장례는 장례식장 입장에서는 수익이 크게 발생하지 않는 장례 형태에요.\n그래서 진행이 불가능한 장례식장이 많아요."
          }
        </MubinsoInfoBox>
      </MubinsoScreenLayout>
    );
  }

  if (screen === "info2") {
    return (
      <MubinsoScreenLayout
        progress={{ current: 2, total: TOTAL_STEPS }}
        contentBottomGap={56}
        footer={
          <>
            <Box
              width="100%"
              textAlign="center"
              whiteSpace="pre-line"
              className="body_14_r gray600"
            >
              희망하시는 지역을 알려주시면
              <br />
              <b style={{ color: colors.gray800 }}>가능한 장례식장을 찾아드릴게요.</b>
            </Box>
            <MubinsoCtaButton
              label="다음"
              onClick={() => {
                customEvent({
                  newGtm: { path: "mubinso_funnel", trigger_and_target: "funnel-next-2" },
                });
                setRegionStep(1);
                setScreen("region");
              }}
            />
          </>
        }
      >
        <Box width="100%" whiteSpace="pre-line" className="head2_b black">
          장례식장 별
          <br />
          차이도 있어요
        </Box>
        <Spacing gap={32} />
        <MubinsoInfoBox>
          장례식장 별 시설 이용료 차이도 있으니,{" "}
          <b style={{ color: colors.gray900 }}>정확한 비용은 확인이 필요</b>해요.
        </MubinsoInfoBox>
      </MubinsoScreenLayout>
    );
  }

  if (screen === "region") {
    const items = regionStep === 1 ? SIDO_LIST : sigunguList;
    return (
      <MubinsoScreenLayout>
        {/* 시군구 단계에선 시도를 잘못 골랐을 수 있어 이전(지역 다시 선택) 제공 */}
        {regionStep === 2 && (
          <>
            <Box
              as="button"
              onClick={() => setRegionStep(1)}
              alignSelf="flex-start"
              display="inline-flex"
              alignItems="center"
              gap="4px"
              border={`1px solid ${colors.gray300}`}
              borderRadius="8px"
              padding="6px 12px"
              bg={colors.white}
              className="body_14_m gray700"
              cursor="pointer"
            >
              <ColorIcon name="icon-arrow-left" size={16} color={colors.gray700} />
              이전
            </Box>
            <Spacing gap={16} />
          </>
        )}
        <Box width="100%" whiteSpace="pre-line" className="head2_b black">
          어디에서 장례를
          <br />
          치르고자 하시나요?
        </Box>
        <Spacing gap={14} />
        <Box width="100%" className="body_16_r gray600">
          선택하신 지역에서 <b style={{ color: colors.orange650 }}>무빈소 진행이 가능한 장례식장</b>
          을 알려드릴게요.
        </Box>
        <Spacing gap={28} />
        <Box width="100%" display="grid" gridTemplateColumns="repeat(3, 1fr)" gridGap="10px">
          {items.map((item) => (
            <Box
              key={`${item.code}-${item.name}`}
              as="button"
              height="52px"
              borderRadius="12px"
              border={`1.5px solid ${colors.gray200}`}
              bg={colors.white}
              className="body_14_m gray800"
              cursor="pointer"
              _hover={{ borderColor: colors.orange650, color: colors.orange650 }}
              onClick={() => {
                customEvent({
                  newGtm: { path: "mubinso_funnel", trigger_and_target: "region-answer" },
                });
                if (regionStep === 1) {
                  setSido({ code: item.code, name: item.name });
                  setRegionStep(2);
                } else {
                  setRegion(`${sido?.name ?? ""} ${item.name}`.trim());
                  setSigunguCode(item.code);
                  // 지역을 다시 고르면 이전 선택은 버린다. 다른 지역 장례식장이 리드에 실리면 안 된다
                  setSelectedParlorIds([]);
                  /*
                   * B안이라도 그 시군구에 장례식장이 한 곳도 없으면 전환으로 바로 보낸다.
                   * 해당 시군구는 5곳이다(서울 마포구, 경기 과천시, 인천 옹진군, 강원 양양군,
                   * 충남 계룡시). 빈 목록을 세우면 "궁금한 곳을 고르라"는 화면이 아무것도
                   * 제시하지 못한다. #344 가 같은 경우를 목록 대신 안내로 처리한다.
                   */
                  const hasCandidates = parlorOptions.some(
                    (parlor) => String(parlor.sigungu) === String(item.code),
                  );
                  setScreen(isPickVariant && hasCandidates ? "parlorPick" : "conversion");
                }
              }}
            >
              {item.name}
            </Box>
          ))}
        </Box>
        <Spacing gap={40} />
      </MubinsoScreenLayout>
    );
  }

  if (screen === "parlorPick") {
    return (
      <MubinsoParlorPickScreen
        regionLabel={region}
        parlors={pickCandidates}
        selectedIds={selectedParlorIds}
        /*
         * 토글할 id 만 넘기고 함수형 setState 로 받는다. 바뀐 배열 전체를 넘기면 클릭 시점
         * props 로 계산돼 빠르게 두 곳을 누를 때 앞 선택이 덮인다(C안에서 잡은 버그).
         */
        onToggle={(id) =>
          setSelectedParlorIds((prev) =>
            prev.includes(id) ? prev.filter((prevId) => prevId !== id) : [...prev, id],
          )
        }
        onNext={() => setScreen("conversion")}
      />
    );
  }

  // conversion
  return (
    <>
      <MubinsoConversionScreen
        entry={entry}
        headline={
          <>
            무빈소 가능한 장례식장,
            <br />
            고이에서 <span style={{ color: colors.orange650 }}>무료로</span> 알려드릴게요
          </>
        }
        checkItems={[
          { text: "무빈소 가능한 전국 장례식장 정보" },
          { text: "추천 장례식장과 예상 비용" },
          { text: "상황 발생 시 자세한 절차" },
        ]}
        /*
         * 고른 장례식장은 체크 항목을 건드리지 않고 따로 되짚는다.
         *
         * 처음에는 첫 체크 항목을 장례식장 이름으로 갈아끼웠는데, 세 항목은 "무엇을 받는지"를
         * 나란히 말하는 자리라 그중 하나만 성격이 달라지면 목록이 흐트러진다. 받는 내용은
         * 고른 것과 무관하게 셋 그대로다.
         *
         * 되짚기 줄은 고른 장례식장 사진을 겹쳐 세운다. 이 퍼널의 진입 의도가 "장례식장을
         * 찾고싶어요" 라, 방금 사진을 보고 고른 행위와 이어지려면 같은 사진이 다시 보여야 한다.
         */
        recap={<MubinsoPickedParlorsRecap parlors={selectedParlors} />}
        reviewTitle="실제 무빈소 진행 가족분들의 후기"
        reviews={[
          {
            text: "“서울 시내에 무빈소를 받는 장례식장이 드물었는데, 어머님이 계신 요양병원과 가까운 곳을 찾아주셨어요.”",
            meta: "2026. 02. 28 작성 후기 · 박OO 고객님",
          },
          {
            text: "“무빈소 장례지만 깊은 감동을 받았어요. 추가금 없이 진행해 주셨습니다.”",
            meta: "2026. 06. 30 작성 후기 · 김OO 고객님",
          },
        ]}
        primaryLabel="장례식장 정보 받아보기"
        onPrimary={() => {
          customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: "conv-info_cta" } });
          if (entry) {
            customEvent({
              newGtm: {
                path: "mubinso_funnel",
                trigger_and_target: mubinsoEntryTrigger(entry, "cta_main"),
              },
            });
          }
          setOpenModal("info");
        }}
        secondaryLabel="무료 상담 신청하기"
        onSecondary={() => {
          customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: "conv-cnslt_cta" } });
          setOpenModal("consult");
        }}
      />

      <MubinsoLeadModal
        isOpen={openModal === "info"}
        onClose={() => setOpenModal(null)}
        title="무빈소 가능한 장례식장 정보 받아보기"
        freeTag
        description={
          family
            ? `${FAMILY_TRACK_RECORD}의 경험을 바탕으로,\n희망하시는 지역에서 진행 가능한\n장례식장과 예상 비용을 정리해서 보내드릴게요.`
            : "무빈소 장례 1,000건 이상의 경험을 바탕으로,\n희망하시는 지역에서 진행 가능한\n장례식장과 예상 비용을 정리해서 보내드릴게요."
        }
        buttonText="동의하고 자료 받아보기"
        messageBuilder={() =>
          buildMubinsoLeadMessage({
            ctaType: "info",
            situation: situationAnswer,
            topic: topicAnswer,
            region,
            parlors: selectedParlorNames,
          })
        }
        leadStatus={mubinsoLeadStatus(situationAnswer)}
        entry={entry}
        onSubmitted={onComplete}
      />
      <MubinsoLeadModal
        isOpen={openModal === "consult"}
        onClose={() => setOpenModal(null)}
        title="무빈소 장례 무료 상담받기"
        description={
          "진행 가능한 장례식장과 예상 비용, 절차를\n빠르게 안내해드려요.\n\n정보만 받아가셔도 괜찮아요.\n준비하시는 데 최대한 도움드릴게요."
        }
        urgentNote={isUrgent}
        buttonText="지금 무료 상담받기"
        messageBuilder={() =>
          buildMubinsoLeadMessage({
            ctaType: "consult",
            situation: situationAnswer,
            topic: topicAnswer,
            region,
            parlors: selectedParlorNames,
          })
        }
        leadStatus={mubinsoLeadStatus(situationAnswer)}
        entry={entry}
        onSubmitted={onComplete}
      />
    </>
  );
}
